import { TabStyles, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { Messages } from '../../../../gen-src/Localize';
import { divWithStyle, OtherDivProps } from './style/LayoutComponents';
import { elideTextStyle } from './style/LayoutStyles';
import { classes, cssClass, Style } from './style/StyleFunctions';
import { assertUnreachable } from './utils/TypescriptHelpers';

const TextWithIconContainer = divWithStyle('TextWithIconContainer', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
});

const flexTextStyle: Style = { flex: 1 };

const PrefixText = divWithStyle('PrefixText', {
  paddingRight: TabStyles.Spacing.SmallSpace
});

const SuffixText = divWithStyle('SuffixText', {
  paddingLeft: TabStyles.Spacing.SmallSpace
});

const SingleLineTextWithIconText = divWithStyle('SingleLineTextWithIconText', [elideTextStyle, flexTextStyle]);

// Multi-line for most browsers, this will elide the text to single line for IE only since it does not support break-word
const MultiLineTextWithIconText = divWithStyle('MultiLineTextWithIconText', [flexTextStyle, {
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word'
}]);

const TextWithIconIcon = divWithStyle('TextWithIconIcon', {
  display: 'flex', // Necessary for the div to size correctly to its child icon
  flexShrink: 0,
  paddingRight: TabStyles.Spacing.SmallSpace
});

interface IconProps {
  readonly iconComponent?: JSX.Element | null;
  readonly iconTitle?: string | null;
  readonly iconSize: IconSize;
}

export type IconSize = 'small' | 'medium'; // Small is 18x18, medium is 24x24

// Text that appears before the icon and after the icon's text/link in a TextWithIcon
// or a LinkWithIcon container.
export interface PrefixSuffixInterface {
  readonly prefixText?: string | null;
  readonly suffixText?: string | null;
}

export interface TextWithIconProps extends IconProps, PrefixSuffixInterface {
  readonly text: string | null;
}

export interface LinkWithIconProps extends IconProps, PrefixSuffixInterface {
  readonly linkText: string | null;
  readonly linkHref: string;
}

/** How to align icons with multiline text */
export type IconPosition = 'top' | 'middle';
export interface MultiLineProps {
  readonly iconPosition: IconPosition;
}

function iconTitleProps(props: IconProps): OtherDivProps {
  const outputProps: OtherDivProps = {};
  if (props.iconTitle) {
    outputProps.title = props.iconTitle;
  }

  return outputProps;
}

function getAlignItemsStyle(iconPosition: IconPosition): React.CSSProperties {
  switch (iconPosition) {
    case 'top':
      return { alignItems: 'flex-start' };
    case 'middle':
      return { alignItems: 'center' };
    default:
      assertUnreachable(iconPosition);
      return {};
  }
}

const ObfuscatedPlaceholder = divWithStyle('ObfuscatedPlaceholder', [elideTextStyle, {
  fontStyle: 'italic',
  color: TabStyles.Typography.Color.Secondary,
  paddingTop: TabStyles.Spacing.SmallSpace, // For alignment with the "Connects to" label
}]);

export function ViewerPaneObfuscatedPlaceholder(): JSX.Element {
  return (
    <ObfuscatedPlaceholder
      data-tb-test-id={'viewer-pane-obfuscated-placeholder-test-id'}
    >
      {Messages.PermissionsRequired()}
    </ObfuscatedPlaceholder>
  );
}

function prefixElement(prefixText: string | null | undefined): JSX.Element | null {
  return prefixText ? <PrefixText>{prefixText}</PrefixText> : null;
}

function suffixElement(suffixText: string | null | undefined): JSX.Element | null {
  return suffixText ? <SuffixText>{suffixText}</SuffixText> : null;
}

export function getPaddingTopStyleForTextWithIcon(iconProps: Pick<IconProps, 'iconSize'>): React.CSSProperties {
  const noPaddingTop: React.CSSProperties = { paddingTop: 0 };
  const smallPaddingTop: React.CSSProperties = { paddingTop: '3px' };
  switch (iconProps.iconSize) {
    case 'small':
      return noPaddingTop;
    case 'medium':
      return smallPaddingTop;
    default:
      assertUnreachable(iconProps.iconSize);
      return {};
  }
}

export function SingleLineTextWithIcon(props: TextWithIconProps): JSX.Element {
  if (props.text === null) {
    return ViewerPaneObfuscatedPlaceholder();
  }

  return (
    <TextWithIconContainer>
      {prefixElement(props.prefixText)}
      {props.iconComponent ? <TextWithIconIcon {...iconTitleProps(props)}>{props.iconComponent}</TextWithIconIcon> : null}
      <SingleLineTextWithIconText
        title={props.text}
        style={getPaddingTopStyleForTextWithIcon(props)}
      >
        {props.text}
      </SingleLineTextWithIconText>
      {suffixElement(props.suffixText)}
    </TextWithIconContainer>
  );
}

export function MultiLineTextWithIcon(props: TextWithIconProps & MultiLineProps): JSX.Element {
  if (props.text === null) {
    return ViewerPaneObfuscatedPlaceholder();
  }

  const alignItemsStyle = getAlignItemsStyle(props.iconPosition);
  return (
    <TextWithIconContainer style={alignItemsStyle}>
      {prefixElement(props.prefixText)}
      {props.iconComponent ? <TextWithIconIcon {...iconTitleProps(props)}>{props.iconComponent}</TextWithIconIcon> : null}
      <MultiLineTextWithIconText
        title={props.text}
        style={getPaddingTopStyleForTextWithIcon(props)}
      >
        {props.text}
      </MultiLineTextWithIconText>
      {suffixElement(props.suffixText)}
    </TextWithIconContainer>
  );
}

export function SingleLineLinkWithIcon(props: LinkWithIconProps): JSX.Element {
  if (props.linkText === null) {
    return ViewerPaneObfuscatedPlaceholder();
  }

  return (
    <TextWithIconContainer>
      {prefixElement(props.prefixText)}
      {props.iconComponent ? <TextWithIconIcon{...iconTitleProps(props)}>{props.iconComponent}</TextWithIconIcon> : null}
      <SingleLineTextWithIconText
        title={props.linkText}
        style={getPaddingTopStyleForTextWithIcon(props)}
      >
        <TextLinkWidget href={props.linkHref} target='_blank' testId='singleLineLinkWithIcon'>{props.linkText}</TextLinkWidget>
      </SingleLineTextWithIconText>
      {suffixElement(props.suffixText)}
    </TextWithIconContainer>
  );
}

export function SingleLineLinkWithSubLinkIcon(props: LinkWithIconProps & { subLinkText: string | null, subLinkHref: string, subPrefixText?: string | null, subSuffixText?: string | null }): JSX.Element {
  if (props.linkText === null) {
    return ViewerPaneObfuscatedPlaceholder();
  }

  return (
    <TextWithIconContainer>
      {prefixElement(props.prefixText)}
      {props.iconComponent ? <TextWithIconIcon{...iconTitleProps(props)}>{props.iconComponent}</TextWithIconIcon> : null}
      <SingleLineTextWithIconText
        title={props.linkText}
        style={getPaddingTopStyleForTextWithIcon(props)}
      >
        <TextLinkWidget href={props.linkHref} target='_blank' testId='sinle-line-link-with-icon'>{props.linkText}</TextLinkWidget>
        {props.subPrefixText}
        <TextLinkWidget href={props.subLinkHref} target='_blank' testId='single-line-with-icon-sublink'>{props.subLinkText}</TextLinkWidget>
        {props.subSuffixText}
      </SingleLineTextWithIconText>
      {suffixElement(props.suffixText)}
    </TextWithIconContainer>
  );
}

export function MultiLineLinkWithIcon(props: LinkWithIconProps & MultiLineProps): JSX.Element {
  if (props.linkText === null) {
    return ViewerPaneObfuscatedPlaceholder();
  }

  const alignItemsStyle = getAlignItemsStyle(props.iconPosition);
  return (
    <TextWithIconContainer style={alignItemsStyle}>
      {prefixElement(props.prefixText)}
      {props.iconComponent ? <TextWithIconIcon {...iconTitleProps(props)}>{props.iconComponent}</TextWithIconIcon> : null}
      <MultiLineTextWithIconText
        title={props.linkText}
        style={getPaddingTopStyleForTextWithIcon(props)}
      >
        <TextLinkWidget href={props.linkHref} target='_blank' testId='multiLineLinkWithIcon'>{props.linkText}</TextLinkWidget>
      </MultiLineTextWithIconText>
      {suffixElement(props.suffixText)}
    </TextWithIconContainer>
  );
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace MetadataTable {

  export interface TableProps {
    readonly valueCellFlexGrow: number;
    readonly children: React.ReactNode;
  }

  const valueCellFlexGrowClass = (valueCellFlexGrow: number): string => {
    return cssClass(`valueCellFlexGrow_${valueCellFlexGrow}`, {
      '$nest': {
        [`.${ValueCellClass}`]: {
          flex: valueCellFlexGrow,
          minWidth: '0px'
        }
      }
    });
  };

  export function Table(props: TableProps): JSX.Element {
    const classNames = classes('MetadataTable', valueCellFlexGrowClass(props.valueCellFlexGrow));
    return (
      <div className={classNames}>
        {props.children}
      </div>
    );
  }

  export const Row = divWithStyle('MetadataTableRow', {
    display: 'flex',
    marginTop: TabStyles.Spacing.DefaultSpace,
  });

  const LabelCellStyle: Style = {
    flex: 1,
    flexShrink: 1,
    color: TabStyles.Typography.Color.Secondary,
    paddingRight: TabStyles.Spacing.MediumSpace,
  };

  export const LabelCell = divWithStyle('MetadataTableLabelCell', LabelCellStyle);

  const ValueCellClass = cssClass('MetadataTableValueCell');

  export function ValueCell(props: { children: React.ReactNode, 'data-tb-test-id'?: string }): JSX.Element {
    return <div className={ValueCellClass} data-tb-test-id={props['data-tb-test-id']}>{props.children}</div>;
  }
}
